import React from 'react';
import { Divider, Box } from '@wix/design-system';
import { withTranslation } from '@wix/fe-essentials/i18n';
import { newMobileViewBreakpoint } from '../../core';
import { useElementOnScreen } from '../../utils/hooks/useElementOnScreen';
import RecurringInvoiceView from '../RecurringInvoicesView/RecurringInvoicesView';
import Status from '../InvoiceViewV2/Status';
import { FooterNotes, InfoLine, InfoSection, InvoiceHeader, InvoiceHighlight, InvoiceSummary, LineItem, } from './components';
import s from './styles.scss';
import { useMobileInvoiceView } from './useHooks';
import { formatBusinessAddress, formatCustomerAddresses } from './utils';
import { PaymentsSection } from './components/PaymentsSection';
const HideOnPrint = ({ children }) => (React.createElement(Box, { className: "hide-on-print" },
    React.createElement("style", null, `@media print {.hide-on-print{display: none;}}`),
    children));
export const DEFAULT_LAYOUT_COLOR = '#F0F2F5';
const MobileInvoiceView = ({ businessInfo, onInvoiceReady, recurringInvoiceViewEnabled = false, renderInvoiceActions, showTaxBreakdown, showTaxPerLineItem, showItemsSubtotalPerTax, showTransactionDetails, taxAdjustmentsEnabled, paymentsSectionEnabled, primarySiteLocale, t, ...rest }) => {
    const { businessDetailsCustomFieldsContent, dateFormatter, documentCustomFieldsContent, dueAmount, footerNotesProps, invoiceDetails, invoiceNumberLabel, invoiceSummaryProps, moneyFormatter, orderReference, showInvoiceHighlight, showInvoiceStatus, } = useMobileInvoiceView({
        ...rest,
        recurringInvoiceViewEnabled,
        t,
    });
    const [divContainerRef, isInvoiceHighlightVisible] = useElementOnScreen(true);
    const renderInvoiceDetails = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement(InfoLine, { type: "heading", paddingTop: "24px" }, invoiceNumberLabel),
            React.createElement(InfoLine, { paddingTop: "8px", weight: "bold" }, orderReference),
            React.createElement(InfoLine, { paddingTop: "8px", weight: "bold" }, invoiceDetails.metaData.sourceProperties?.referenceText),
            showInvoiceStatus && (React.createElement(HideOnPrint, null,
                React.createElement(Box, { marginTop: "8px", marginBottom: "8px" },
                    React.createElement(Status, { isUoU: true, isOutlined: true, status: invoiceDetails.status })))),
            React.createElement(InfoSection, { title: invoiceDetails.title, content: [
                    invoiceDetails.issueDate
                        ? `${t('quote.form.issue_date')}: ${dateFormatter(new Date(invoiceDetails.issueDate))}`
                        : undefined,
                    invoiceDetails.dueDate
                        ? `${t('quote.form.due_date')}: ${dateFormatter(new Date(invoiceDetails.dueDate))}`
                        : undefined,
                ] }),
            React.createElement(InfoSection, { marginTop: "12px", content: documentCustomFieldsContent }),
            React.createElement(InfoSection, { marginTop: "24px", title: businessInfo.name, content: [
                    businessInfo.email,
                    businessInfo.phone
                        ? t('price-quote.view.business.phone', {
                            phone: businessInfo.phone,
                        })
                        : undefined,
                    ...(businessInfo?.businessAddress?.address !== undefined
                        ? formatBusinessAddress(businessInfo.businessAddress, t)
                        : []),
                    businessInfo.companyId
                        ? t('price-quote.view.business.company-id', {
                            companyId: businessInfo.companyId,
                        })
                        : undefined,
                ] }),
            React.createElement(InfoSection, { marginTop: "12px", content: businessDetailsCustomFieldsContent }),
            formatCustomerAddresses(invoiceDetails.customer, t).map((infoSectionProps, index) => (React.createElement(InfoSection, { key: `info-section-${index}`, marginTop: index === 0 ? '24px' : '16px', ...infoSectionProps })))));
    };
    const renderLineItems = () => {
        if (!invoiceDetails.lineItems.length) {
            return undefined;
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(InfoLine, { type: "heading", paddingTop: "24px", paddingBottom: "24px" }, t('price-quote.view.line-items.products-and-services-heading')),
            invoiceDetails.lineItems.map((lineItem, index, { length }) => (React.createElement(LineItem, { key: `LineItem_${index}`, formatMoney: moneyFormatter, isLast: index === length - 1, lineItem: lineItem, showTax: showTaxPerLineItem, t: t })))));
    };
    /** TODO: missing design */
    const renderRecurringInvoiceView = () => recurringInvoiceViewEnabled && (React.createElement(RecurringInvoiceView, { darkView: false, 
        // TODO: abstract this away once rewritten
        invoice: rest.invoice, isReady: true, newMobileView: true, recurringStandaloneViewEnabled: false }));
    const renderFooterNotes = () => {
        const FooterNotesComponent = React.createElement(FooterNotes, { ...footerNotesProps, t: t });
        return FooterNotesComponent ? (React.createElement(React.Fragment, null,
            React.createElement(Box, { marginTop: "20px", height: "12px", background: DEFAULT_LAYOUT_COLOR }),
            FooterNotesComponent)) : null;
    };
    return (React.createElement(Box, { maxWidth: `${newMobileViewBreakpoint}px`, direction: "vertical", paddingBottom: "66px" },
        React.createElement(InvoiceHeader, { background: DEFAULT_LAYOUT_COLOR, businessName: businessInfo.name, logo: businessInfo.logo, onImageReady: onInvoiceReady }),
        renderRecurringInvoiceView(),
        React.createElement(Box, { backgroundColor: "white", direction: "vertical", paddingLeft: "5%", paddingRight: "5%", paddingBottom: "20px" },
            showInvoiceHighlight && (React.createElement(React.Fragment, null,
                React.createElement(InvoiceHighlight, { amountPaid: invoiceDetails.paid?.toString?.()
                        ? moneyFormatter(invoiceDetails.paid)
                        : undefined, dueAmount: moneyFormatter(dueAmount), dueDate: invoiceDetails.dueDate
                        ? dateFormatter(invoiceDetails.dueDate)
                        : undefined, t: t }, renderInvoiceActions?.({ hidePdfDownloader: true })),
                React.createElement("div", { ref: divContainerRef },
                    React.createElement(Box, { marginTop: "24px" },
                        React.createElement(Divider, null))))),
            renderInvoiceDetails(),
            React.createElement(Box, { marginTop: "24px" },
                React.createElement(Divider, null)),
            renderLineItems(),
            React.createElement(InvoiceSummary, { ...invoiceSummaryProps, formatMoney: moneyFormatter, showTaxBreakdown: showTaxBreakdown, taxAdjustmentsEnabled: taxAdjustmentsEnabled, showItemsSubtotalPerTax: showItemsSubtotalPerTax, t: t }),
            paymentsSectionEnabled && showTransactionDetails && (React.createElement(PaymentsSection, { invoice: invoiceDetails, primarySiteLocale: primarySiteLocale })),
            renderFooterNotes()),
        (!isInvoiceHighlightVisible || !showInvoiceHighlight) && (React.createElement("div", { className: `${s.floatingButton} ${s.appearAnimation}` }, renderInvoiceActions?.({
            hidePdfDownloader: false,
            mobileInvoiceViewV2Enabled: true,
        })))));
};
export const MobileInvoicePreview = withTranslation([
    'invoice-view',
    'locale-data',
])(MobileInvoiceView);
//# sourceMappingURL=MobileInvoiceView.js.map